// Page initialization and event listener setup

import { clearActiveResearchIdOnServer, getCurrentResearchId } from '../state/research.js';
import { setPendingSearchQuery } from './signin-modal.js';
import { initializePublicationYearFilter } from './publication-year-filter.js';
import { closeSignInModal } from './signin-modal.js';
import { handleCognitoCallback, setAuthCallbacks, handleLogout, checkAuthStatus, updateSignOutUI } from './auth.js';
import { showSnackbar } from './snackbar.js';
import {
    openResearchHistory,
    openPreferences,
    closePreferences,
    closeResearchHistory,
    showMainSidebar
} from './sidebar.js';
import { loadSharedResearch } from './research-session-loader.js';
import { applyPreferences, loadAndApplyAppearance } from './preferences.js';
import { getAuthHeaders, getSubscriptionStatusUrl, authenticatedFetch } from '../api/auth.js';
import { updateFreePlanRemainingMessage, updateFairUseThresholdMessage } from './search-handler.js';

// Function to reset the page to initial state (same as "New Search" button)
export async function resetToInitialState() {
    await clearActiveResearchIdOnServer();
    // Note: setCurrentResearchId will be passed as a callback
    if (window.bms && window.bms.setCurrentResearchId) {
        window.bms.setCurrentResearchId(null);
    }
    sessionStorage.removeItem('bms_session_id');
    sessionStorage.removeItem('bms_has_searched');
    // Navigate to root
    window.location.href = '/';
}

// Set up callback for resetToInitialState to use setCurrentResearchId
let setCurrentResearchIdCallback = null;

// Handle session expiry: clear auth state, show message, redirect to sign-in
window.addEventListener('sessionExpired', () => {
    updateSignOutUI();
    sessionStorage.setItem('bms_session_expired', '1');
    window.location.href = '/';
});

// Check anonymous search limit (removed - no longer disabling textarea on page load)
async function checkAnonymousSearchLimit() {
    // This function is kept for potential future use but doesn't disable textarea
    // The limit is now checked after each search and shows a modal instead
}

export function setInitializationCallbacks(callbacks) {
    if (callbacks.setCurrentResearchId) {
        setCurrentResearchIdCallback = callbacks.setCurrentResearchId;
        // Also expose on window.bms for resetToInitialState
        window.bms = window.bms || {};
        window.bms.setCurrentResearchId = setCurrentResearchIdCallback;
    }
    if (callbacks.resetToInitialState) {
        // Override resetToInitialState with the callback if provided
        resetToInitialState = callbacks.resetToInitialState;
    }
}

// Initialize page on DOMContentLoaded
export function initializePage(handlers) {
    const { handleSearch, setCurrentResearchId } = handlers || {};
    
    // Set up callback for setCurrentResearchId
    if (setCurrentResearchId) {
        setCurrentResearchIdCallback = setCurrentResearchId;
        window.bms = window.bms || {};
        window.bms.setCurrentResearchId = setCurrentResearchId;
    }
    
    // Attach global click listener to close userQuery menus (once)
    if (!window.userQueryMenuListenerAttached) {
        document.addEventListener('click', (event) => {
            if (!event.target.closest('.userQuery-actions')) {
                document.querySelectorAll('.userQuery-actions-menu.open').forEach(menu => {
                    menu.classList.remove('open');
                });
            }
        });
        window.userQueryMenuListenerAttached = true;
    }

    // Suppress native title tooltip on icon buttons (use custom tooltip only)
    if (!window.iconBtnTooltipListenerAttached) {
        document.addEventListener('mouseover', (e) => {
            const btn = e.target.closest('.icon-btn');
            if (btn && btn.dataset.tooltip && !e.relatedTarget?.closest('.icon-btn')) {
                btn.setAttribute('data-title-backup', btn.title || '');
                btn.title = '';
            }
        });
        document.addEventListener('mouseout', (e) => {
            const btn = e.target.closest('.icon-btn');
            if (btn && !e.relatedTarget?.closest('.icon-btn')) {
                const backup = btn.getAttribute('data-title-backup');
                if (backup !== null) {
                    btn.title = backup;
                    btn.removeAttribute('data-title-backup');
                }
            }
        });
        window.iconBtnTooltipListenerAttached = true;
    }
    
    document.addEventListener('DOMContentLoaded', async () => {
        // Show friendly message if redirected due to session expiry
        if (sessionStorage.getItem('bms_session_expired')) {
            sessionStorage.removeItem('bms_session_expired');
            showSnackbar('Your session expired. Please sign in again.', 'info', 'warning', 5000);
        }

        const navigationEntries = typeof performance !== 'undefined' && performance.getEntriesByType
            ? performance.getEntriesByType('navigation')
            : [];
        const isReload = Array.isArray(navigationEntries) && navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
        if (isReload) {
            await clearActiveResearchIdOnServer();
            if (setCurrentResearchId) {
                setCurrentResearchId(null);
            }
        }
        
        // Restore pending search query from sessionStorage if it exists (after sign-in redirect)
        const storedPendingQuery = sessionStorage.getItem('bms_pending_search_query');
        if (storedPendingQuery && localStorage.getItem('access_token')) {
            // User signed in and page reloaded, restore the pending query
            setPendingSearchQuery(storedPendingQuery);
            sessionStorage.removeItem('bms_pending_search_query');
        }

        // Check for share URL: /share/<research_id>
        const shareMatch = window.location.pathname.match(/^\/share\/([^/]+)$/);
        if (shareMatch) {
            const researchId = shareMatch[1];
            loadSharedResearch(researchId);
            // Continue with auth/UI setup; loadSharedResearch runs async
        }

        initializePublicationYearFilter();

        // Load and apply appearance theme
        loadAndApplyAppearance();
        
        // Check search count for anonymous users
        checkAnonymousSearchLimit();

        // Show free-plan / fair-use messages on main search page (skip on share page – shared links are viewable without login)
        const isSharePage = window.location.pathname.startsWith('/share/');
        if (!isSharePage) {
            (async () => {
                try {
                    if (typeof window.bms?.checkSearchLimit === 'function') {
                        await window.bms.checkSearchLimit({ fromInit: true });
                    } else {
                        updateFreePlanRemainingMessage(null);
                        updateFairUseThresholdMessage(false);
                    }
                } catch (e) {
                    updateFreePlanRemainingMessage(null);
                    updateFairUseThresholdMessage(false);
                }
            })();
        }

        // Logo/brand link click handler (navigates home)
        const homeLinks = document.querySelectorAll('.brand-home-link');
        homeLinks.forEach((homeLink) => {
            homeLink.addEventListener('click', async (e) => {
                e.preventDefault();
                if (setCurrentResearchId) {
                    await clearActiveResearchIdOnServer();
                    setCurrentResearchId(null);
                    sessionStorage.removeItem('bms_session_id');
                    sessionStorage.removeItem('bms_has_searched');
                    window.location.href = '/';
                } else {
                    await resetToInitialState();
                }
            });
        });

        // Sign in button - directly call Cognito
        const signInActionBtn = document.getElementById('signinActionBtn');
        if (signInActionBtn) {
            // Wait for auth module to be ready
            function setupSignInButton() {
                if (window.cognitoAuth && window.cognitoAuth.signInRedirect) {
                    signInActionBtn.addEventListener('click', async (e) => {
                        e.preventDefault();
                        try {
                            await window.cognitoAuth.signInRedirect();
                        } catch (error) {
                            console.error('[Cognito] Sign-in error:', error);
                            alert('Failed to initiate sign-in: ' + error.message);
                        }
                    });
                } else {
                    // Wait a bit and try again
                    setTimeout(setupSignInButton, 100);
                }
            }
            
            // Try immediately, or wait for ready event
            if (window.cognitoAuth && window.cognitoAuth.isReady) {
                setupSignInButton();
            } else {
                window.addEventListener('cognitoAuthReady', setupSignInButton, { once: true });
                // Fallback: try after a delay
                setTimeout(setupSignInButton, 1000);
            }
        }

        // Close modal on close button click
        const closeBtn = document.getElementById('closeSignInModal');
        if (closeBtn) {
            closeBtn.addEventListener('click', closeSignInModal);
        }

        // Continue without Sign-In button handler
        const continueWithoutSignInBtn = document.getElementById('continueWithoutSignInBtn');
        if (continueWithoutSignInBtn) {
            continueWithoutSignInBtn.addEventListener('click', closeSignInModal);
        }

        // Close modal on overlay click
        const modal = document.getElementById('signInModal');
        if (modal) {
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    closeSignInModal();
                }
            });
        }

        // Upgrade to Pro button handler
        const upgradeToProBtn = document.getElementById('upgradeToProBtn');
        if (upgradeToProBtn) {
            upgradeToProBtn.addEventListener('click', () => {
                window.location.href = '/pricing';
            });
        }
        
        // Manage Subscription button handler
        const manageSubscriptionBtn = document.getElementById('manageSubscriptionBtn');
        if (manageSubscriptionBtn) {
            manageSubscriptionBtn.addEventListener('click', async () => {
                try {
                    const response = await fetch('/api/auth/stripe-portal', {
                        headers: getAuthHeaders()
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        if (data.status === 'success' && data.portal_url) {
                            // Redirect to Stripe customer portal
                            window.location.href = data.portal_url;
                        } else {
                            alert('Failed to load subscription management. Please try again.');
                        }
                    } else if (response.status === 401) {
                        // Token expired, try refresh
                        const refreshResponse = await fetch('/api/auth/refresh', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                refresh_token: localStorage.getItem('refresh_token')
                            })
                        });
                        
                        if (refreshResponse.ok) {
                            const refreshData = await refreshResponse.json();
                            if (refreshData.access_token) {
                                localStorage.setItem('access_token', refreshData.access_token);
                                // Retry portal URL request
                                const retryResponse = await fetch('/api/auth/stripe-portal', {
                                    headers: {
                                        'X-Access-Token': refreshData.access_token
                                    }
                                });
                                
                                if (retryResponse.ok) {
                                    const retryData = await retryResponse.json();
                                    if (retryData.status === 'success' && retryData.portal_url) {
                                        window.location.href = retryData.portal_url;
                                        return;
                                    }
                                }
                            }
                        }
                        
                        alert('Please sign in to manage your subscription.');
                    } else {
                        const errorData = await response.json();
                        alert(errorData.message || 'Failed to load subscription management. Please try again.');
                    }
                } catch (error) {
                    console.error('[Subscription] Error loading portal:', error);
                    alert('An error occurred. Please try again.');
                }
            });
        }

        // AWS Cognito Sign-In button handler (in modal)
        const cognitoSignInBtn = document.getElementById('cognitoSignInBtn');
        if (cognitoSignInBtn) {
            // Wait for auth module to be ready
            function setupCognitoButton() {
                if (window.cognitoAuth && window.cognitoAuth.signInRedirect) {
                    cognitoSignInBtn.addEventListener('click', async () => {
                        try {
                            await window.cognitoAuth.signInRedirect();
                        } catch (error) {
                            console.error('[Cognito] Sign-in error:', error);
                            alert('Failed to initiate sign-in: ' + error.message);
                        }
                    });
                } else {
                    // Wait a bit and try again
                    setTimeout(setupCognitoButton, 100);
                }
            }
            
            // Try immediately, or wait for ready event
            if (window.cognitoAuth && window.cognitoAuth.isReady) {
                setupCognitoButton();
            } else {
                window.addEventListener('cognitoAuthReady', setupCognitoButton, { once: true });
                // Fallback: try after a delay
                setTimeout(setupCognitoButton, 1000);
            }
        }

        // Handle Cognito sign-in callback on page load (only if returning from OAuth redirect)
        // Check for both code and state parameters to ensure valid OAuth callback
        const hasOAuthParams =
            (window.location.search.includes('code=') || window.location.hash.includes('code=')) &&
            (window.location.search.includes('state=') || window.location.hash.includes('state='));
        
        if (hasOAuthParams) {
            handleCognitoCallback();
        } else {
            const params = new URLSearchParams(window.location.search);
            const error = params.get('error');
            const errorDesc = (params.get('error_description') || '').replace(/\+/g, ' ');
            if (error && (errorDesc.includes('already exists') || errorDesc.includes('Please sign in instead'))) {
                showSnackbar('Account already exists\nThis email is already registered using email & password.\nPlease sign in using that method.', 'error', 'warning');
                window.history.replaceState({}, document.title, window.location.pathname + (window.location.hash || ''));
            }
        }

        // Set up auth callbacks
        if (handleSearch) {
            setAuthCallbacks({
                handleSearch
            });
        }

        // Run auth status check only after DOM is ready so account elements exist.
        await checkAuthStatus();
    });

    // Add logout button click handler
    document.addEventListener('DOMContentLoaded', () => {
        const logoutBtn = document.getElementById('logoutBtn');
        if (logoutBtn) {
            logoutBtn.addEventListener('click', async (e) => {
                e.preventDefault();
                const resetFn = async () => {
                    await clearActiveResearchIdOnServer();
                    if (setCurrentResearchId) {
                        setCurrentResearchId(null);
                    }
                    sessionStorage.removeItem('bms_session_id');
                    sessionStorage.removeItem('bms_has_searched');
                    window.location.href = '/';
                };
                await handleLogout(resetFn);
            });
        }
    });

    // Close modal on Escape key (global listener)
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            const modal = document.getElementById('signInModal');
            if (modal && !modal.classList.contains('hidden')) {
                closeSignInModal();
            }
        }
    });

    // Sidebar click handler - close sidebar when clicking outside
    document.addEventListener('click', function(event) {
        // Check if the clicked element is not a button inside the toolbar
        const sidebar = document.getElementById('sidebar');
        const researchSidebar = document.getElementById('researchHistorySidebar');
        const preferencesSidebar = document.getElementById('preferencesSidebar');

        // If the click was inside the research history, preferences sidebar, share modal, or delete confirm modal, don't close the main sidebar
        const shareModal = document.getElementById('shareResearchModal');
        const deleteConfirmModal = document.getElementById('deleteConfirmModal');
        if ((researchSidebar && researchSidebar.contains(event.target)) ||
            (preferencesSidebar && preferencesSidebar.contains(event.target)) ||
            (shareModal && shareModal.contains(event.target)) ||
            (deleteConfirmModal && deleteConfirmModal.contains(event.target))) {
            return;
        }

        if (sidebar && sidebar.classList.contains('open') && !sidebar.contains(event.target) && !event.target.closest('.toggle-btn')) {
            sidebar.classList.remove('open');
        }
    });

    // Add event listeners for Research History
    document.addEventListener('DOMContentLoaded', () => {
        const researchHistoryBtn = document.getElementById('researchHistoryBtn');
        const closeResearchHistoryBtn = document.getElementById('closeResearchHistoryBtn');
        
        if (researchHistoryBtn) {
            researchHistoryBtn.addEventListener('click', (e) => {
                e.preventDefault();
                openResearchHistory();
            });
        }
        
        if (closeResearchHistoryBtn) {
            closeResearchHistoryBtn.addEventListener('click', (e) => {
                e.preventDefault();
                showMainSidebar();
            });
        }
        
        // Preferences button handler
        const preferencesBtn = document.getElementById('preferencesBtn');
        if (preferencesBtn) {
            preferencesBtn.addEventListener('click', (e) => {
                e.preventDefault();
                openPreferences();
            });
        }
        
        const closePreferencesBtn = document.getElementById('closePreferencesBtn');
        if (closePreferencesBtn) {
            closePreferencesBtn.addEventListener('click', (e) => {
                e.preventDefault();
                showMainSidebar();
            });
        }
        
        // Apply preferences button handler
        const applyPreferencesBtn = document.getElementById('applyPreferencesBtn');
        if (applyPreferencesBtn) {
            applyPreferencesBtn.addEventListener('click', async (e) => {
                e.preventDefault();
            // Get currentResearchId from state
            const currentResearchId = getCurrentResearchId();
            await applyPreferences(currentResearchId);
            });
        }
        
        // Close preferences sidebar when clicking outside
        document.addEventListener('click', (e) => {
            const preferencesSidebar = document.getElementById('preferencesSidebar');
            if (preferencesSidebar && preferencesSidebar.classList.contains('open') && !preferencesSidebar.contains(e.target) && 
                !e.target.closest('#preferencesBtn')) {
                closePreferences();
            }
        });
        
        // Close research history sidebar when clicking outside
        document.addEventListener('click', (e) => {
            const sidebar = document.getElementById('researchHistorySidebar');
            const shareModal = document.getElementById('shareResearchModal');
            const deleteConfirmModal = document.getElementById('deleteConfirmModal');
            if (sidebar && sidebar.classList.contains('open') && !sidebar.contains(e.target) && 
                !e.target.closest('#researchHistoryBtn') &&
                !(shareModal && shareModal.contains(e.target)) &&
                !(deleteConfirmModal && deleteConfirmModal.contains(e.target))) {
                closeResearchHistory();
            }
        });
    });
}
